import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2, Rows2, RadioTower } from 'lucide-react';
import { useTerminalExtStore, tabPanes } from '../../store/terminalExtStore';
import { countLeaves } from './paneTreeModel';
import TermIconButton from './TermIconButton';
import { splitDisabledText } from './TerminalPaneHeader';
import { chordKeys } from './terminalPaneShortcuts';
import { useSplitWithMenu } from './SplitWithMenu';

const WARN_MS = 4000;

/**
 * Right-aligned actions for the active tab: split right / split down (of the
 * focused pane — each opens "Split with…"), `layoutsMenu` (phase 3) and Broadcast input. Disabled
 * buttons stay visible with the reason in their tooltip.
 */
export default function TerminalTabToolbar({ tab, layoutsMenu = null }) {
  const { t } = useTranslation();
  const canSplitPane = useTerminalExtStore((s) => s.canSplitPane);
  const { openSplitWith, splitWithMenu } = useSplitWithMenu();
  const setBroadcast = useTerminalExtStore((s) => s.setBroadcast);
  // Re-render on size / pane changes so the disabled states stay honest.
  useTerminalExtStore((s) => (tab ? s.gridSizes[tab.id] : null));
  const allPanes = useTerminalExtStore((s) => s.panes);
  const panes = useMemo(() => (tab ? tabPanes({ panes: allPanes }, tab) : []), [allPanes, tab]);
  const [warn, setWarn] = useState(null);
  const warnTimer = useRef(null);
  useEffect(() => () => clearTimeout(warnTimer.current), []);
  useEffect(() => {
    if (!tab?.broadcast) setWarn(null);
  }, [tab?.broadcast]);

  if (!tab) return null;
  const focused = tab.focusedPaneId;
  const row = canSplitPane(focused, 'row');
  const col = canSplitPane(focused, 'col');
  const multi = countLeaves(tab.layout) > 1;

  let broadcastReason = null;
  if (tab.locked) broadcastReason = t('terminalExt.aiTabNoBroadcast', 'Not available in the AI tab');
  else if (!multi) broadcastReason = t('terminalExt.broadcastNeedsPanes', 'Split the tab first — broadcast types into every pane');

  const toggleBroadcast = () => {
    const on = !tab.broadcast;
    if (!setBroadcast(tab.id, on) || !on) return;
    const agents = panes.filter((p) => p.profileId && p.status === 'running');
    if (agents.length) {
      setWarn(
        t('terminalExt.broadcastWarn', 'Keystrokes go to all {{count}} panes, including {{names}}', {
          count: panes.length,
          names: agents.map((p) => p.title || p.label).join(', '),
        })
      );
      clearTimeout(warnTimer.current);
      warnTimer.current = setTimeout(() => setWarn(null), WARN_MS);
    }
  };

  return (
    <div className="term-tab-toolbar" role="toolbar" aria-label={t('terminalExt.tabToolbar', 'Terminal layout')}>
      <TermIconButton
        className="term-tool-btn"
        label={t('terminalExt.splitRight', 'Split right')}
        keys={chordKeys('splitRight')}
        disabledReason={splitDisabledText(t, row.reason)}
        onClick={(e) => openSplitWith(focused, 'row', e.currentTarget)}
      >
        <Columns2 size={15} />
      </TermIconButton>
      <TermIconButton
        className="term-tool-btn"
        label={t('terminalExt.splitDown', 'Split down')}
        keys={chordKeys('splitDown')}
        disabledReason={splitDisabledText(t, col.reason)}
        onClick={(e) => openSplitWith(focused, 'col', e.currentTarget)}
      >
        <Rows2 size={15} />
      </TermIconButton>
      {layoutsMenu}
      <span className="term-tool-sep" aria-hidden />
      <TermIconButton
        className={`term-tool-btn${tab.broadcast ? ' term-tool-btn--on' : ''}`}
        label={tab.broadcast ? t('terminalExt.broadcastOn', 'Broadcast input: on') : t('terminalExt.broadcast', 'Broadcast input to all panes')}
        disabledReason={broadcastReason}
        pressed={!!tab.broadcast}
        onClick={toggleBroadcast}
      >
        <RadioTower size={15} />
      </TermIconButton>
      {splitWithMenu}
      {warn && (
        <div className="term-broadcast-warn" role="alert">
          {warn}
        </div>
      )}
    </div>
  );
}
